import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '@/services/subscription/subscription.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, Globe, Server, User, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

export default function SubscriptionDetailPage() {
    const { subscriptionId } = useParams<{ subscriptionId: string }>();
    const navigate = useNavigate();

    const { data: subscription, isLoading, error } = useQuery({
        queryKey: ['subscription', 'admin', subscriptionId],
        queryFn: () => subscriptionService.getSubscriptionById(subscriptionId!),
        enabled: !!subscriptionId,
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'SUSPENDED':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'CANCELLED':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'EXPIRED':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-64" />
                <div className="grid md:grid-cols-2 gap-6">
                    <Skeleton className="h-48" />
                    <Skeleton className="h-48" />
                </div>
                <Skeleton className="h-64" />
            </div>
        );
    }

    if (error || !subscription) {
        return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        Failed to load subscription details. Please try again later.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate(-1)} size="sm">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold">Subscription Details</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            ID: {subscriptionId}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* User Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <User className="h-5 w-5" />
                            User Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Name</p>
                            <p className="font-medium">
                                {subscription.user?.firstName} {subscription.user?.lastName}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Email</p>
                            <p className="font-medium">{subscription.user?.email}</p>
                        </div>
                        {subscription.user?.phone && (
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Phone</p>
                                <p className="font-medium">{subscription.user.phone}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Company Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Building2 className="h-5 w-5" />
                            Company Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Company Name</p>
                            <p className="font-medium">{subscription.company?.name}</p>
                        </div>
                        {subscription.company?.website && (
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Website</p>
                                <p className="font-medium">{subscription.company.website}</p>
                            </div>
                        )}
                        {subscription.company?.address && (
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Address</p>
                                <p className="font-medium">{subscription.company.address}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Subscription Info */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Subscription Plan</CardTitle>
                        <Badge className={getStatusColor(subscription.status)}>
                            {subscription.status}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Plan</p>
                            <p className="text-xl font-semibold">{subscription.plan?.displayName}</p>
                            <p className="text-sm text-slate-500">
                                ${(subscription.plan?.priceInCents ?? 0).toFixed(2)}/month
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Period Start</p>
                            <p className="font-medium">
                                {format(new Date(subscription.currentPeriodStart), 'PPP')}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Period End</p>
                            <p className="font-medium">
                                {format(new Date(subscription.currentPeriodEnd), 'PPP')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Deployment Info */}
            {subscription.deployment && (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Server className="h-5 w-5" />
                                Deployment
                            </CardTitle>
                            <Badge className={getStatusColor(subscription.deployment.status)}>
                                {subscription.deployment.status}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-slate-400" />
                                    <span className="text-sm text-slate-500">Frontend:</span>
                                    <span className="font-mono text-sm">{subscription.deployment.frontendDomain}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-slate-400" />
                                    <span className="text-sm text-slate-500">Backend:</span>
                                    <span className="font-mono text-sm">{subscription.deployment.backendDomain}</span>
                                </div>
                                {subscription.deployment.filesDomain && (
                                    <div className="flex items-center gap-2">
                                        <Globe className="h-4 w-4 text-slate-400" />
                                        <span className="text-sm text-slate-500">Files:</span>
                                        <span className="font-mono text-sm">{subscription.deployment.filesDomain}</span>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <p>
                                    <span className="text-sm text-slate-500">IP Address: </span>
                                    <span className="font-mono">{subscription.deployment.ipAddress}</span>
                                </p>
                                <p>
                                    <span className="text-sm text-slate-500">Created: </span>
                                    {format(new Date(subscription.deployment.createdAt), 'PP')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/deployments/${subscription.deployment?.id}`)}
                            >
                                Edit Deployment
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {!subscription.deployment && (
                <Card>
                    <CardContent className="flex items-center justify-between py-6">
                        <div>
                            <p className="font-medium">No Deployment</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                This subscription does not have a deployment configured yet.
                            </p>
                        </div>
                        {subscription.status === 'ACTIVE' && (
                            <Button onClick={() => navigate('/admin/deployments/create')}>
                                Create Deployment
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Recent Payments */}
            {subscription.payments && subscription.payments.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Recent Payments</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {subscription.payments.map((payment: any) => (
                                <div
                                    key={payment.id}
                                    className="flex items-center justify-between py-2 border-b last:border-0"
                                >
                                    <div>
                                        <p className="font-medium">
                                            ${payment.amountInCents.toFixed(2)} {payment.currency}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                            {payment.invoiceNumber || 'No invoice'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <Badge
                                            className={
                                                payment.status === 'SUCCESSFUL'
                                                    ? 'bg-green-100 text-green-800'
                                                    : payment.status === 'PENDING'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-red-100 text-red-800'
                                            }
                                        >
                                            {payment.status}
                                        </Badge>
                                        <p className="text-sm text-slate-500 mt-1">
                                            {format(new Date(payment.createdAt), 'PP')}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
